
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowUpRight, Eye, MousePointerClick, SearchX, Timer } from 'lucide-react';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { cn } from '@/lib/utils';
import { useAnalyticsOverview } from '@/hooks/use-analytics-overview';
import {
  AnalyticsPageChrome,
  AnalyticsPanel,
  AnalyticsPeriodBadge,
} from '@/components/painel/analytics/AnalyticsPageChrome';
import Link from '@/components/compat/Link';
import { usePainelText } from '@/components/painel/painel-texts-context';

function formatInt(n: number) {
  return new Intl.NumberFormat('pt-BR').format(n);
}

export function PainelAnalyticsOverviewPage() {
  const pageText = usePainelText('analytics_visao');
  const { data } = useAnalyticsOverview();
  const totalVisits = data.visitsWeek || data.visitsSeries.reduce((a, d) => a + d.visits, 0);

  return (
    <AnalyticsPageChrome
      title={pageText.title}
      description={pageText.description}
      actions={
        <>
          <AnalyticsPeriodBadge>Últimos 7 dias</AnalyticsPeriodBadge>
          <Link
            href="/painel/analytics/trafego"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2',
              'font-poppins text-xs font-semibold text-blue-100 transition-all hover:border-sky-400/35 hover:bg-white/[0.09] hover:text-white'
            )}
          >
            Ver tráfego
            <ArrowUpRight className="h-3.5 w-3.5 opacity-80" />
          </Link>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <PainelStatCard
          title="Visitantes"
          value={formatInt(totalVisits)}
          icon={Eye}
          description="Sessões únicas estimadas"
          trend={data.visitsTrend ?? undefined}
          variant="sky"
        />
        <PainelStatCard
          title="Taxa de interação"
          value={data.interactionRateLabel}
          icon={MousePointerClick}
          description="Cliques nos resultados de busca"
          trend={data.interactionTrend ?? undefined}
          variant="cyan"
        />
        <PainelStatCard
          title="Tempo médio"
          value={data.avgSessionDurationLabel}
          icon={Timer}
          description="Por sessão no centro de ajuda"
          trend={data.durationTrend ?? undefined}
          variant="indigo"
        />
        <PainelStatCard
          title="Consultas sem resultado"
          value={data.zeroResultRateLabel}
          icon={SearchX}
          description="Buscas que não abriram um guia"
          trend={data.zeroResultTrend ?? undefined}
          variant="violet"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <AnalyticsPanel
          className="lg:col-span-3"
          title="Tráfego ao longo da semana"
          subtitle="Comparativo com período anterior (linha tracejada)"
        >
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.visitsSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="ovVisits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fill: 'rgba(191,219,254,0.5)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'rgba(191,219,254,0.4)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15,23,42,0.96)',
                    border: '1px solid rgba(56,189,248,0.28)',
                    borderRadius: 10,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: '#e2e8f0' }}
                  formatter={(value: number, name: string) => [formatInt(value), name === 'visits' ? 'Visitas' : 'Anterior']}
                />
                <Area
                  type="monotone"
                  dataKey="prev"
                  name="prev"
                  stroke="#64748b"
                  strokeDasharray="4 4"
                  fill="none"
                  strokeWidth={1.5}
                />
                <Area
                  type="monotone"
                  dataKey="visits"
                  name="visits"
                  stroke="#38bdf8"
                  strokeWidth={2}
                  fill="url(#ovVisits)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </AnalyticsPanel>

        <AnalyticsPanel
          className="lg:col-span-2"
          title="Canal de aquisição"
          subtitle="Partilha estimada das sessões"
        >
          <div className="flex h-[280px] flex-col items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.channels}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={56}
                  outerRadius={86}
                  paddingAngle={2}
                >
                  {data.channels.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} stroke="rgba(15,23,42,0.9)" strokeWidth={1} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15,23,42,0.96)',
                    border: '1px solid rgba(56,189,248,0.28)',
                    borderRadius: 10,
                    fontSize: 12,
                  }}
                  formatter={(value: number) => [`${value}%`, '']}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                  formatter={(value) => <span className="text-slate-300">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </AnalyticsPanel>
      </div>

      <AnalyticsPanel
        title="Pesquisas em destaque"
        subtitle="Termos que mais impulsionam aberturas de guias"
        headerRight={
          <Link
            href="/painel/analytics/pesquisas"
            className="text-xs font-semibold text-sky-300/90 hover:text-sky-200"
          >
            Explorar pesquisas →
          </Link>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.topQueries.slice(0, 6).map((q) => (
            <div
              key={q.query}
              className={cn(
                'rounded-xl border border-white/[0.07] bg-white/[0.035] px-4 py-3',
                'transition-colors hover:border-sky-400/25'
              )}
            >
              <p className="truncate font-poppins text-sm font-medium text-white">{q.query}</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="font-inter text-xs tabular-nums text-slate-400">{formatInt(q.count)} eventos</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 font-poppins text-[10px] font-semibold',
                    q.trend >= 0
                      ? 'bg-emerald-500/15 text-emerald-200/90'
                      : 'bg-rose-500/12 text-rose-200/85'
                  )}
                >
                  {q.trend >= 0 ? '+' : ''}
                  {q.trend}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </AnalyticsPanel>
    </AnalyticsPageChrome>
  );
}
